import axios from 'axios';
import { shopApi } from 'api';

import {
  PROMO_GROUP_ID,
  RECOMMENDS_GROUP_ID,
  MAIN_GROUP_ID,
  axiosGetXsollaItemsConfig,
  currencySymbolsMap,
} from '../consts';
import type { Currency } from '../consts';
import { Locale } from 'localisation';
import { trackEvent } from 'analytics/amplitude';

// type SpecialGroupsSpecialExternalIds = 'promo' | 'recommends' | 'special' | 'test' | 'main';
const SPECIAL_GROUPS = [
  PROMO_GROUP_ID,
  RECOMMENDS_GROUP_ID,
  // SPECIAL_GROUP_ID
];

type SpecialGroupsAccumulator = {
  [key: string]: {};
};

const generateSpecialGroupsObject = (): SpecialGroupsAccumulator => {
  return SPECIAL_GROUPS.reduce<SpecialGroupsAccumulator>((acc, groupId) => {
    acc[groupId] = {};
    return acc;
  }, {});
};

const isWithinRange = (from: string, to: string) => {
  const currentDate = new Date();
  return currentDate >= new Date(from) && currentDate <= new Date(to);
};

const filterEnabledItems = (items: shopApi.Item[] = []) => {
  return items.filter(item => {
    const itemPeriods = item.periods || [];
    const inPeriod = itemPeriods.length
      ? itemPeriods.some(period =>
          isWithinRange(period.date_from, period.date_until!),
        )
      : true;

    return inPeriod && item.can_be_bought;
  });
};

const sortAndFilterGroups = (groups: XsollaFormattedItems) => {
  return groups
    .filter(itemGroup => itemGroup.items.length > 0)
    .sort((a, b) => a.order - b.order);
};

type XsollaItems = {
  order: number;
  items: shopApi.Item[];
  group: shopApi.Group;
};
type XsollaGroupedItems = Record<string, XsollaItems>;
export type XsollaFormattedItems = {
  externalId: string;
  order: number;
  items: shopApi.Item[];
  group: shopApi.Group;
}[];

export async function collectItemsByGroups({
  externalId,
  lang,
  country,
  promocode,
}: {
  externalId?: string;
  country?: string | null;
  lang?: Locale;
  promocode?: string;
} = {}): Promise<XsollaFormattedItems> {
  try {
    const xsollaItemsResponse = await axios(
      axiosGetXsollaItemsConfig(externalId, country, lang, promocode),
    );

    const xsollaItems = xsollaItemsResponse.data as XsollaGroupedItems;
    const formattedItems = [] as XsollaFormattedItems;

    for (const key in xsollaItems) {
      if (xsollaItems.hasOwnProperty(key)) {
        formattedItems.push({
          externalId: key,
          order: xsollaItems[key].order,
          items: filterEnabledItems(xsollaItems[key].items),
          group: xsollaItems[key].group,
        });
      }
    }

    // sort and filter groups by order if externalId is not specified
    return externalId ? formattedItems : sortAndFilterGroups(formattedItems);
  } catch (error) {
    console.error('Error collecting items by groups:', error);
    throw error;
  }
}

export type SpecialGroup = {
  items: shopApi.Item[];
  name: string;
  order: number;
  externalId: string;
};

export interface SpecialGroups {
  [key: string]: SpecialGroup | SpecialGroup[];
}

export const makeGroupsSpecial = (groups: XsollaFormattedItems): SpecialGroups => {
  const specialGroupsObject = generateSpecialGroupsObject();

  const groupedItems: SpecialGroups = {
    ...specialGroupsObject,
    [MAIN_GROUP_ID]: [],
  };

  groups?.forEach(({ externalId, items, group }) => {
    const groupExternalId = group.external_id;
    const groupKey = SPECIAL_GROUPS?.includes(groupExternalId)
      ? groupExternalId
      : MAIN_GROUP_ID;

    const itemToInsert = {
      name: group.name,
      order: group.order,
      externalId: groupExternalId,
      items: items,
    };

    if (Array.isArray(groupedItems[groupKey])) {
      (groupedItems[groupKey] as SpecialGroup[]).push(itemToInsert);
    } else {
      groupedItems[groupKey] = itemToInsert;
    }
  });

  return groupedItems;
};

export const sortAndFilterMainGroup = (groupedItems: SpecialGroups) => {
  const mainGroupedItemsFilteredByOrder = Object.values(
    groupedItems[MAIN_GROUP_ID],
  ).sort((a, b) => a.order - b.order);

  return mainGroupedItemsFilteredByOrder;
};

export const groupItems = (groups: XsollaFormattedItems): SpecialGroups => {
  const groupedItems = makeGroupsSpecial(groups);

  const mainGroupedItemsFilteredByOrder = sortAndFilterMainGroup(groupedItems);

  return { ...groupedItems, main: mainGroupedItemsFilteredByOrder };
};

export const getItemPrice = (
  item: shopApi.Item,
  amount: string,
  t: Record<string, any>,
) => {
  const currency = item?.price?.currency as Currency;
  const currencySymbol = currencySymbolsMap[currency || 'USD'];

  const price = amount ? `${amount} ${currencySymbol}` : t.market.item.free;

  return price;
};

export const trackItemClick = (item: shopApi.Item) => {
  trackEvent('item_view', {
    itemId: item.sku,
    itemName: item.name,
    itemGroup: item.groups.map(group => group.name).join(', '),
  });
};
